import { invalidFields } from '@ovComponents/4-module/workflowCompletion/subSteps/utils';
import { buildDepositFieldOptions } from '@ovComponents/4-module/workflowCompletion/subSteps/utils/depositFieldOptions';
import { useDepositSchedule } from '@ovComponents/4-module/workflowCompletion/subSteps/utils/useDepositSchedule';
import type { useDepositWorkflowData } from '@ovComponents/4-module/workflowCompletion/subSteps/utils/useDepositWorkflowData';
import { pick } from 'lodash-es';
import { useState } from 'react';
import type { MultiDepositFormProp } from '../MultiDepositForm';
import { useDepositEligibleSubAccounts } from './useDepositEligibleSubAccounts';

type DepositWorkflowData = ReturnType<typeof useDepositWorkflowData>;

export type UseMultiDepositEftProps = Pick<MultiDepositFormProp, 'options'> & {
  deposit: DepositWorkflowData['applicableData'];
  updateContextWithFields: DepositWorkflowData['updateContextWithFields'];
  userId?: string;
};

const VALIDATED_FIELDS = [
  'amountCents',
  'bankAccount',
  'schedule',
  'scheduledDate',
];

export const useMultiDepositEft = ({
  options,
  deposit,
  updateContextWithFields,
  userId,
}: UseMultiDepositEftProps) => {
  const [accountId, setAccountId] = useState('');

  const { accounts, loading } = useDepositEligibleSubAccounts(userId);

  const selectedEligibleAccount = accounts.find(
    ({ account }) => account.id === accountId,
  );

  const schedule = useDepositSchedule({
    options,
    deposit,
    updateContextWithFields,
    userId,
  });

  const missingFields = invalidFields(
    pick(
      buildDepositFieldOptions({
        options: options ?? {},
        applicableData: deposit,
        starting: schedule.starting,
        participantNumberEnabled: false,
        isMultiCurrencyInternalTransfer: false,
        hasAvailableCurrencies: false,
      }),
      VALIDATED_FIELDS,
    ),
    deposit,
  );

  const selectAccount = (id: string) => {
    setAccountId(id);
    updateContextWithFields({ bankAccount: undefined });
  };

  return {
    accounts,
    loading,
    accountId,
    selectedAccount: selectedEligibleAccount?.account,
    subAccounts: selectedEligibleAccount?.subAccounts ?? [],
    schedule,
    missingFields,
    selectAccount,
  };
};
